import { useState, useEffect, useCallback } from 'react'
import { fetchBiometricRecords } from '../api/biometricRecordsApi'
import { appToast } from '../../../../components/AppToast'

export default function useBiometricRecords() {
  const [date, setDate] = useState(() => new Date().toISOString().split('T')[0])
  const [records, setRecords] = useState([])
  const [loading, setLoading] = useState(false)
  const [isReportsOpen, setIsReportsOpen] = useState(false)
  const [sortBy, setSortBy] = useState('rawTime')
  const [sortOrder, setSortOrder] = useState('asc')

  // Filters
  const [searchText, setSearchText] = useState('')
  const [searchField, setSearchField] = useState('All')
  const [companyFilter, setCompanyFilter] = useState('All')
  const [branchFilter, setBranchFilter] = useState('All')
  const [courseFilter, setCourseFilter] = useState('All')
  const [schoolFilter, setSchoolFilter] = useState('All')
  const [academicYearFilter, setAcademicYearFilter] = useState('2026-2027')
  const [batchFilter, setBatchFilter] = useState('All')
  const [subjectFilter, setSubjectFilter] = useState('All')

  // Metrics
  const [metrics, setMetrics] = useState({
    totalSwipes: 0,
    uniqueStudents: 0,
    inSwipesCount: 0,
    outSwipesCount: 0,
    earliestSwipe: '—',
    latestSwipe: '—',
  })

  // Load Data
  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const filters = {
        searchText,
        searchField,
        company: companyFilter,
        branch: branchFilter,
        course: courseFilter,
        school: schoolFilter,
        academicYear: academicYearFilter,
        batch: batchFilter,
        subject: subjectFilter,
      }
      const res = await fetchBiometricRecords(date, filters, sortBy, sortOrder)
      setRecords(res.records)
      setMetrics(res.metrics)
    } catch (e) {
      console.error('Failed to load biometric swipe records', e)
      appToast.error('Failed to load biometric records.')
    } finally {
      setLoading(false)
    }
  }, [
    date,
    searchText,
    searchField,
    companyFilter,
    branchFilter,
    courseFilter,
    schoolFilter,
    academicYearFilter,
    batchFilter,
    subjectFilter,
    sortBy,
    sortOrder,
  ])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Sorting
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
  }

  // Date Navigation Helpers
  const handlePrevDate = () => {
    const current = new Date(date)
    current.setDate(current.getDate() - 1)
    setDate(current.toISOString().split('T')[0])
  }

  const handleNextDate = () => {
    const current = new Date(date)
    current.setDate(current.getDate() + 1)
    setDate(current.toISOString().split('T')[0])
  }

  const handleToday = () => {
    setDate(new Date().toISOString().split('T')[0])
  }

  const handleYesterday = () => {
    const yest = new Date()
    yest.setDate(yest.getDate() - 1)
    setDate(yest.toISOString().split('T')[0])
  }

  // Reset Filters
  const handleResetFilters = () => {
    setSearchText('')
    setSearchField('All')
    setCompanyFilter('All')
    setBranchFilter('All')
    setCourseFilter('All')
    setSchoolFilter('All')
    setAcademicYearFilter('2026-2027')
    setBatchFilter('All')
    setSubjectFilter('All')
    appToast.info('Filters have been reset.')
  }

  // Export
  const handleExportExcel = () => {
    appToast.success(`Exporting biometric raw swipe log for ${date}...`)
  }

  // SMS
  const handleSendSMS = () => {
    appToast.info(`Dispatched biometric in/out swipe logs via SMS to parents.`)
  }

  // Options
  const options = {
    searchFields: [
      { value: 'All', label: 'All Fields' },
      { value: 'Student', label: 'Student Name' },
      { value: 'RollNo', label: 'Roll Number' },
      { value: 'IdCardNo', label: 'ID Card No' },
      { value: 'Course', label: 'Course' },
      { value: 'Batch', label: 'Batch' },
      { value: 'Branch', label: 'Branch' },
      { value: 'Company', label: 'Company' },
    ],
    companies: [
      { value: 'All', label: 'All Companies' },
      { value: 'Tez Educational Trust', label: 'Tez Educational Trust' },
      { value: 'Tez Educational School', label: 'Tez Educational School' },
      { value: 'Tolani College Of Commerce', label: 'Tolani College Of Commerce' },
    ],
    branches: [
      { value: 'All', label: 'All Branches' },
      { value: 'Dadar', label: 'Dadar' },
      { value: 'Thane', label: 'Thane' },
      { value: 'Andheri', label: 'Andheri' },
      { value: 'Borivali', label: 'Borivali' },
    ],
    courses: [
      { value: 'All', label: 'All Courses' },
      { value: 'CA Foundation', label: 'CA Foundation' },
      { value: 'CS Executive', label: 'CS Executive' },
      { value: 'JEE Advanced', label: 'JEE Advanced' },
      { value: 'NEET UG', label: 'NEET UG' },
    ],
    schools: [
      { value: 'All', label: 'All Schools' },
      { value: 'Main Campus', label: 'Main Campus' },
      { value: 'East Annex', label: 'East Annex' },
      { value: 'North Campus', label: 'North Campus' },
    ],
    batches: [
      { value: 'All', label: 'All Batches' },
      { value: 'Batch-A (Morning)', label: 'Batch-A (Morning)' },
      { value: 'Batch-CS-01', label: 'Batch-CS-01' },
      { value: 'Super-30 (Intensive)', label: 'Super-30 (Intensive)' },
      { value: 'Med-Achievers', label: 'Med-Achievers' },
    ],
    subjects: [
      { value: 'All', label: 'All Subjects' },
      { value: 'Principles and Practice of Accounting', label: 'Accounting' },
      { value: 'Company Law & Practice', label: 'Company Law' },
      { value: 'Physics', label: 'Physics' },
      { value: 'Mathematics', label: 'Mathematics' },
      { value: 'Biology (Botany)', label: 'Biology' },
      { value: 'Chemistry', label: 'Chemistry' },
    ],
    academicYears: [
      { value: 'All', label: 'All Years' },
      { value: '2026-2027', label: '2026-2027' },
      { value: '2025-2026', label: '2025-2026' },
    ],
  }

  return {
    state: {
      date,
      records,
      loading,
      isReportsOpen,
      sortBy,
      sortOrder,
      metrics,
      searchText,
      searchField,
      companyFilter,
      branchFilter,
      courseFilter,
      schoolFilter,
      academicYearFilter,
      batchFilter,
      subjectFilter,
      options,
    },
    actions: {
      setDate,
      setIsReportsOpen,
      setSearchText,
      setSearchField,
      setCompanyFilter,
      setBranchFilter,
      setCourseFilter,
      setSchoolFilter,
      setAcademicYearFilter,
      setBatchFilter,
      setSubjectFilter,
      handleSort,
      handlePrevDate,
      handleNextDate,
      handleToday,
      handleYesterday,
      handleResetFilters,
      handleExportExcel,
      handleSendSMS,
    },
  }
}
